import {
	AlignHorizontalJustifyCenter,
	AlignVerticalJustifyCenter,
	AlignVerticalJustifyEnd,
	AlignVerticalJustifyStart,
	Aperture,
	ArchiveRestore,
	ArrowDown,
	ArrowDownAZ,
	ArrowLeft,
	ArrowLeftRight,
	ArrowRight,
	ArrowUp,
	Baby,
	Bath,
	BedDouble,
	Box,
	Briefcase,
	Building,
	Building2,
	CalendarDays,
	Camera,
	Check,
	ChevronDown,
	ChevronUp,
	CircleAlert,
	CircleHelp,
	Code,
	CookingPot,
	Copy,
	DoorClosed,
	DoorOpen,
	Download,
	Expand,
	ExternalLink,
	Eye,
	EyeOff,
	FileText,
	Filter,
	Flower2,
	Folder,
	Footprints,
	Frame,
	Gem,
	Grid2x2,
	Grid3x3,
	House,
	ImagePlus,
	Key,
	Lamp,
	LayoutTemplate,
	LibraryBig,
	Lock,
	Map,
	MonitorSmartphone,
	MoreHorizontal,
	MoreVertical,
	MousePointer2,
	Mountain,
	Move,
	MoveHorizontal,
	Palette,
	Pencil,
	PencilRuler,
	Plus,
	Redo2,
	RefreshCw,
	RotateCw,
	Ruler,
	Save,
	SaveAll,
	Scissors,
	Search,
	Settings,
	Share2,
	Shirt,
	Sofa,
	Rows3,
	SquareSplitVertical,
	Star,
	Sun,
	TreePine,
	Trees,
	TriangleAlert,
	Trash2,
	Undo2,
	User,
	UserPlus,
	Utensils,
	Wallpaper,
	Wrench,
	X,
	type LucideIcon,
} from "lucide-react";

/**
 * The icon set, keyed by the names the app already uses.
 *
 * Keeping the old `…Icon` keys is deliberate: migrating a call site off
 * `components/ui` becomes a one-line import change rather than a rename, and the
 * key stays a checked union so a typo is a compile error, not a blank square.
 *
 * Lucide is the source (§6 wants 24px outline, 1.8px stroke, round caps and
 * joins, `currentColor` — Lucide is all of that natively, with the stroke width
 * set by `Icon`). Where a hegias icon is a domain category with no exact Lucide
 * counterpart, the nearest honest reading is used and noted; those are the ones
 * worth a second opinion on `/dev/design`.
 *
 * The two logo entries are **not** here. A brand mark is not an icon: it does not
 * inherit `currentColor`, does not live on a 24px grid, and must not be swapped
 * for a generic glyph. They stay with `components/ui` until the logo assets are
 * re-exported (see the plan's §0).
 */
export const iconRegistry = {
	/* --- navigation and chrome --- */
	ArrowBottomIcon: ArrowDown,
	ArrowTopIcon: ArrowUp,
	ArrowLeftIcon: ArrowLeft,
	ArrowRightIcon: ArrowRight,
	ArrowBottom2Icon: ChevronDown,
	ArrowTop2Icon: ChevronUp,
	ArrowDownFilledIcon: ChevronDown,
	CloseIcon: X,
	EllipsisIcon: MoreHorizontal,
	EllipsisVerticalIcon: MoreVertical,
	SearchIcon: Search,
	ExternalLinkIcon: ExternalLink,
	LibraryIcon: LibraryBig,
	FilterIcon: Filter,
	FolderIcon: Folder,
	SettingIcon: Settings,

	/* --- actions --- */
	PlusIcon: Plus,
	CopyIcon: Copy,
	DeleteIcon: Trash2,
	SaveIcon: Save,
	SaveAsIcon: SaveAll,
	RefreshIcon: RefreshCw,
	RestoreIcon: ArchiveRestore,
	PencilIcon: Pencil,
	ShareIcon: Share2,
	EmbedIcon: Code,
	SwitchIcon: ArrowLeftRight,
	DocumentIcon: FileText,
	CalendarIcon: CalendarDays,
	SortAlphabeticalIcon: ArrowDownAZ,

	/* --- feedback --- */
	CorrectIcon: Check,
	ExclamationIcon: CircleAlert,
	WarningIcon: TriangleAlert,
	QuestionIcon: CircleHelp,
	FullFavouriteIcon: Star,

	/* --- identity and access --- */
	UserIcon: User,
	AddUserIcon: UserPlus,
	OrganizationIcon: Building,
	LockIcon: Lock,
	KeyIcon: Key,

	/* --- 3D editor tools --- */
	MoveIcon: Move,
	RotateIcon: RotateCw,
	/** Transform-scale, not a balance — Lucide's `Scale` is the justice scale. */
	ScaleIcon: Expand,
	DistanceIcon: Ruler,
	CrossSectionIcon: SquareSplitVertical,
	WalkingIcon: Footprints,
	CameraIcon: Camera,
	Camera2Icon: Aperture,
	MapIcon: Map,
	PrototypeIcon: Box,
	UndoIcon: Undo2,
	RedoIcon: Redo2,
	DownloadIcon: Download,
	/** Mounting-height alignment, three states. */
	AlignTopIcon: AlignVerticalJustifyStart,
	AlignMiddleIcon: AlignVerticalJustifyCenter,
	AlignBottomIcon: AlignVerticalJustifyEnd,
	/** The two states of the distance-measurement toggle: edge, then centre. */
	DistanceEdgeIcon: MoveHorizontal,
	DistanceCenterIcon: AlignHorizontalJustifyCenter,

	/* --- 2D floorplanner --- */
	/** Select / move mode — an arrow cursor, the tool it turns the pointer into. */
	PointerIcon: MousePointer2,
	/** Drawing walls to scale; a plain pencil would not say "to measure". */
	DrawWallIcon: PencilRuler,
	DoorIcon: DoorClosed,
	/** No architectural window in Lucide; a 4-pane grid is the nearest honest reading. */
	WindowIcon: Grid2x2,
	/** Importing a background image to trace over. */
	ImageAddIcon: ImagePlus,
	/** Hold-to-peek through the background image. */
	EyeIcon: Eye,
	/** A scene file hidden in the Konfigurator (Dateien in der Szene). */
	EyeOffIcon: EyeOff,
	/** A floorplan template — a layout, not a file. */
	TemplateIcon: LayoutTemplate,
	/** Splitting a wall in two. */
	SplitIcon: Scissors,

	/* --- library taxonomy: rooms --- */
	BathroomIcon: Bath,
	KitchenIcon: CookingPot,
	LivingIcon: Sofa,
	SleepingIcon: BedDouble,
	DiningIcon: Utensils,
	ChildrenIcon: Baby,
	WorkingIcon: Briefcase,
	/** No Lucide corridor; a doorway is the closest honest reading. */
	CorridorIcon: DoorOpen,
	OutdoorIcon: Trees,

	/* --- library taxonomy: building elements --- */
	FacadeIcon: Building2,
	RoofIcon: House,
	TechnicalIcon: Wrench,
	DeviceIcon: MonitorSmartphone,
	LampIcon: Lamp,
	EnvironmentIcon: Sun,

	/* --- library taxonomy: materials --- */
	WoodIcon: TreePine,
	StoneIcon: Mountain,
	TilesIcon: Grid3x3,
	CarpetIcon: Rows3,
	TextilesIcon: Shirt,
	WallpaperIcon: Wallpaper,
	/** A pane, read as glass. Lucide has no glass material glyph. */
	GlassIcon: Frame,
	ColorfulIcon: Palette,
	PlantsIcon: Flower2,
	AccessoiresIcon: Gem,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof iconRegistry;

export const iconNames = Object.keys(iconRegistry) as IconName[];
